import { useState } from "react";
import { X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSite } from "@/context/SiteContext";
import { useLang } from "@/i18n/LanguageContext";
import { productById, FREE_CARRIAGE_CENTS, formatShort } from "@/data/catalog";
import { money } from "@/components/CartTotals";
import { WelcomeCode } from "@/components/WelcomeCode";
import { daysLabel } from "@/components/MeasureCalc";

const PAIRS = { foresters_guard: ["trailhead_vigor", "riverbend_calm"], trailhead_vigor: ["foresters_guard"], riverbend_calm: ["foresters_guard"], grey_muzzle_reserve: ["foresters_guard"], discovery_dram: ["foresters_guard"] };
const PAIR_LINE = { trailhead_vigor: "Cordyceps, for the trail.", riverbend_calm: "Reishi, for the watchful.", foresters_guard: "Turkey tail, for every day." };

const Item = ({ id, qty, product }) => {
  const { setQty, remove, weightLb } = useCart();
  const { t } = useLang();
  const lasts = product.id !== "discovery_dram" && weightLb > 0 ? daysLabel(weightLb, t) : "";
  return (
    <div className="it" data-testid={`cart-item-${id}`}>
      <img src={product.image} alt={`${product.name} jar`} />
      <div className="t" data-no-translate>
        <h4>{product.name}</h4>
        <div className="m" data-testid={`cart-lasts-${id}`}>{product.id === "discovery_dram" ? "3 × 10 g" : "60 g"}{lasts ? ` · ${lasts}` : ""}</div>
        <div className="q">
          <button type="button" data-testid={`cart-dec-${id}`} aria-label="Decrease" onClick={() => setQty(id, qty - 1)}><span>−</span></button>
          <span data-testid={`cart-qty-${id}`}>{qty}</span>
          <button type="button" data-testid={`cart-inc-${id}`} aria-label="Increase" onClick={() => setQty(id, qty + 1)}><span>+</span></button>
        </div>
        <button type="button" data-testid={`cart-remove-${id}`} onClick={() => remove(id)} className="muted" style={{ fontSize: 12, marginLeft: 12 }}>{t("Remove", "Retirer")}</button>
      </div>
      <div className="pr">{money(product.priceCents * qty)}</div>
    </div>
  );
};

const Pairs = () => {
  const { items, add } = useCart();
  const have = new Set(items.map((i) => i.id));
  const ids = [...new Set(items.flatMap((i) => PAIRS[i.id] || []))].filter((id) => !have.has(id)).slice(0, 2);
  if (!ids.length) return null;
  return (
    <div data-testid="cart-pairs-well">
      <div className="eyebrow" style={{ marginTop: 18 }}>Pairs well · ships free together</div>
      {ids.map((id) => {
        const p = productById(id);
        return (
          <div className="up" key={id}>
            <img src={p.image} alt="" />
            <div className="t"><b>{p.name} · {formatShort(p.priceCents)}</b>{PAIR_LINE[id]} Two jars save 10%.</div>
            <button type="button" data-testid={`cart-pair-add-${id}`} onClick={() => add(id, 1, false)} className="up-add">Add</button>
          </div>
        );
      })}
    </div>
  );
};

export const CartDrawer = () => {
  const { open, setOpen, items, totalCents, pricing, checkout } = useCart();
  const { orderingOpen, notify } = useSite();
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (!open) return null;
  const onCheckout = async () => {
    if (!orderingOpen) { setOpen(false); notify(); return; }
    setBusy(true); setError("");
    try { await checkout(); } catch (e) { const d = e.response?.data?.detail; setError(typeof d === "string" ? d : "Checkout didn't open. Please try again."); } finally { setBusy(false); }
  };
  const progress = pricing.welcomeApplied ? 100 : Math.min(100, (totalCents / FREE_CARRIAGE_CENTS) * 100);
  return (
    <div className="kit k-cart">
      <div className="dim-bg" onClick={() => setOpen(false)} data-testid="cart-backdrop" />
      <aside className="drawer" role="dialog" aria-label="Your cart" data-testid="cart-drawer">
        <div className="dh"><h3>Your cart</h3><button type="button" onClick={() => setOpen(false)} aria-label="Close cart" data-testid="cart-close-button" className="muted"><X className="icon" /></button></div>
        {items.length === 0 ? (
          <p className="serif" data-testid="cart-empty-message" style={{ padding: 26, fontSize: 22, color: "var(--cream)" }}>Your cart is empty. The dogs disapprove.</p>
        ) : (
          <>
            <div className="meter" data-testid="free-carriage-progress">
              <p data-testid="free-carriage-message" data-no-translate>
                {pricing.toFreeCents > 0 ? <>{t("You're ", "Plus que ")}<b>{money(pricing.toFreeCents)}</b>{t(" from free shipping.", " avant la livraison gratuite.")}</> : t("Your order ships free.", "Votre commande est livrée gratuitement.")}
              </p>
              <div className="bar"><i style={{ width: `${progress}%` }} /></div>
            </div>
            <div className="items" style={{ overflowY: "auto" }}>
              {items.map((i) => <Item key={i.id} {...i} />)}
              <Pairs />
            </div>
            <div className="foot" data-no-translate>
              <WelcomeCode />
              <div className="l"><span>{t("Subtotal", "Sous-total")}</span><b data-testid="cart-subtotal">{money(totalCents)}</b></div>
              {pricing.packPct > 0 && <div className="l" style={{ color: "var(--brass)" }}><span>{t(`Multi-jar savings · ${pricing.packPct}%`, `Rabais multi-pots · ${pricing.packPct} %`)}</span><span data-testid="cart-pack-discount">−{money(pricing.discountCents)}</span></div>}
              {pricing.treatCents > 0 && <div className="l"><span>{t("Birthday treat", "Gâterie d'anniversaire")}</span><span>−{money(pricing.treatCents)}</span></div>}
              <div className="l"><span className="muted">{t("Shipping", "Livraison")}</span><span className="muted" data-testid="cart-shipping">{pricing.shippingCents ? t(`${money(pricing.shippingCents)} · free over $125`, `${money(pricing.shippingCents)} · gratuite dès 125 $`) : t("Free", "Gratuite")}</span></div>
              {error && <p data-testid="cart-checkout-error" style={{ color: "#E39B7B", fontSize: 13 }}>{error}</p>}
              <button type="button" className="btn btn-primary" onClick={onCheckout} disabled={busy} data-testid="cart-checkout-button">
                {!orderingOpen ? t("Ordering opens soon · Notify me", "Commandes bientôt ouvertes · M'avertir") : busy ? t("Opening checkout…", "Ouverture du paiement…") : t(`Checkout · ${money(pricing.grandCents)}`, `Payer · ${money(pricing.grandCents)}`)}
              </button>
              <small>{t("Taxes calculated at checkout · Unopened jars returnable within 30 days", "Taxes calculées au paiement · Pots non ouverts retournables sous 30 jours")}</small>
            </div>
          </>
        )}
      </aside>
    </div>
  );
};
